'use client';

import { type ReactElement, Suspense } from 'react';
import { Await } from 'react-router';
import type { ShopperProducts } from '@/scapi';
import EditorialInlineTruncationText from '@/components/inline-truncation-text/editorial-inline-truncation-text';
import { usePlpHeaderOptimisticState } from '@/components/eataly-category-plp-header/use-plp-header-optimistic-state';
import { SubcategoryTagsContainer } from '@/components/eataly-category-plp-header/subcategory-tags-container';
import { EatalyPlpSubcategoryTagLink } from '@/components/eataly-plp-subcategory-tag';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { SeoLink } from '@/lib/seo-linking/resolve-links';

interface EatalyCategoryPlpHeaderProps {
    category: ShopperProducts.schemas['Category'];
    /** Filtered subcategory / sibling tags (product presence + shortname labels). */
    subcategoryLinksPromise: Promise<SeoLink[] | null>;
    /** True only for in-PLP loading (refine/page/sort or category→category), not PDP navigation. */
    isNavigating: boolean;
}

const subcategoryTagsSkeleton = (
    <div className="flex flex-wrap gap-2 " data-testid="plp-header-subcategories-skeleton">
        <Skeleton className="h-10 w-24 rounded-sm" />
        <Skeleton className="h-10 w-28 rounded-sm" />
        <Skeleton className="h-10 w-20 rounded-sm" />
    </div>
);

/**
 * PLP intro block: title, optional description (2-line clamp + read more when content overflows),
 * subcategory quick filters.
 */
export default function EatalyCategoryPlpHeader({
    category,
    subcategoryLinksPromise,
    isNavigating,
}: EatalyCategoryPlpHeaderProps): ReactElement {
    const { title, description, showDescriptionSkeleton, showSubcategoriesSkeleton, setPendingNavigationTarget } =
        usePlpHeaderOptimisticState(category, isNavigating);
    const showSubcategoryTags = category.c_showCategoryFilter !== true;

    const subcategoryTags = (() => {
        if (!showSubcategoryTags) {
            return null;
        }

        if (showSubcategoriesSkeleton) {
            return subcategoryTagsSkeleton;
        }

        return (
            <Suspense fallback={subcategoryTagsSkeleton}>
                <Await resolve={subcategoryLinksPromise}>
                    {(links) => {
                        const tagLinks = Array.isArray(links) ? links : [];
                        if (tagLinks.length === 0) {
                            return null;
                        }
                        return (
                            <SubcategoryTagsContainer>
                                {tagLinks.map((link) => (
                                    <EatalyPlpSubcategoryTagLink
                                        key={link.id}
                                        label={link.name}
                                        isSelected={false}
                                        to={link.to}
                                        isCurrentPage={false}
                                        onNavigate={() =>
                                            setPendingNavigationTarget({
                                                id: link.id,
                                                name: link.name,
                                            })
                                        }
                                    />
                                ))}
                            </SubcategoryTagsContainer>
                        );
                    }}
                </Await>
            </Suspense>
        );
    })();

    return (
        <div
            className={cn('bg-neutral-white border-b border-sand-90', isNavigating && 'pointer-events-none')}
            aria-busy={isNavigating || undefined}>
            <div className="grid-global gap-y-4 lg:gap-y-0 py-3 lg:py-8 ">
                <div className="col-span-6 lg:col-start-2 lg:col-span-3">
                    <h1 className="heading-display-s text-neutral-10">{title}</h1>
                </div>

                <div className="lg:col-start-5 lg:ps-8 col-span-6 lg:col-span-full flex flex-col gap-4">
                    {showDescriptionSkeleton ? (
                        <div className="flex flex-col w-3/4 gap-2" data-testid="plp-header-description-skeleton">
                            <Skeleton className="h-5 w-full" />
                            <Skeleton className="h-5 w-4/5" />
                        </div>
                    ) : (
                        <EditorialInlineTruncationText text={description} />
                    )}

                    {subcategoryTags}
                </div>
            </div>
        </div>
    );
}
